<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Sale Details</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ sales?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/all-sales')" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <button class="btn-outline" @click="createPDF(sales?.batch_code)" :disabled="loading">
            <font-awesome-icon icon="download" /> PDF
          </button>
          <router-link :to="$url('/print58mm-sales-receipt/' + sales?.id)" class="btn-outline">
            <font-awesome-icon icon="print" /> 58mm
          </router-link>
          <router-link :to="$url('/print80mm-sales-receipt/' + sales?.id)" class="btn-outline">
            <font-awesome-icon icon="print" /> 80mm
          </router-link>
          <router-link :to="$url('/sales-receipt/' + sales?.id)" class="btn-primary-action">
            <font-awesome-icon icon="file-invoice" /> A4 Receipt
          </router-link>
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 5" :key="n"></div>
        </div>
        <div class="skeleton skeleton--table mt-4"></div>
      </div>
  
      <template v-else>
  
        <!-- Summary Card -->
        <div class="form-card mb-4" id="pdf">
  
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Date</span>
              <span class="summary-value">{{ dateTime(sales?.date) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Sales No.</span>
              <span class="summary-value">
                <span class="batch-code">{{ sales?.batch_code }}</span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Store</span>
              <span class="summary-value">
                <span class="store-tag">{{ sales?.store?.store_name }}</span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Customer</span>
              <span class="summary-value">
                <div class="user-cell" v-if="sales?.customer?.customer_name">
                  <div class="user-avatar">{{ sales.customer.customer_name.charAt(0) }}</div>
                  {{ sales.customer.customer_name }}
                </div>
                <span v-else class="text-muted-cell">Walk-in</span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Sold By</span>
              <span class="summary-value">
                <div class="user-cell">
                  <div class="user-avatar">{{ sales?.user?.fullname?.charAt(0) }}</div>
                  {{ sales?.user?.fullname }}
                </div>
              </span>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Items Table -->
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="receipt" />
            </div>
            <div>
              <p class="section-title">Sale Items</p>
              <p class="section-sub">{{ sales_array.length }} item(s) in this sale</p>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Variant / UOM</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Selling Price</th>
                  <th class="text-right">Tax</th>
                  <th class="text-right">Discount</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, index) in sales_array" :key="index">
                  <td class="text-muted-cell">{{ index + 1 }}</td>
                  <td>
                    <div class="product-cell">
                      <div class="product-avatar"><font-awesome-icon icon="box" /></div>
                      <span class="product-name">{{ p?.product_name }}</span>
                    </div>
                  </td>
                  <td class="text-muted-cell">{{ p?.variant_name || '—' }}</td>
                  <td class="text-right">
                    <span class="qty-badge">{{ p?.quantity }}</span>
                  </td>
                  <td class="text-right text-muted-cell">{{ $filters.formatMoney(p?.selling_price) }}</td>
                  <td class="text-right text-muted-cell">{{ $filters.formatMoney(p?.tax) }}</td>
                  <td class="text-right text-muted-cell">{{ $filters.formatMoney(p?.discount) }}</td>
                  <td class="text-right fw-bold amount-value">{{ $filters.formatMoney(p?.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Grand Total -->
          <div class="grand-total-row">
            <span class="grand-total-label">Grand Total</span>
            <span class="grand-total-value">{{ $filters.formatMoney(grandTotal) }}</span>
          </div>
  
        </div>
  
      </template>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from './component/salesheadertab.vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import jsPDF from 'jspdf';
  import html2canvas from 'html2canvas';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    components: { InventoryHeaderTab },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:       useToast(),
        sales:       null,
        sales_items: [],
        sales_array: [],
        loading:     true,
      };
    },
  
    computed: {
      grandTotal() {
        return this.sales_array.reduce((a, b) => a + parseFloat(b.total ?? 0), 0).toFixed(2);
      },
    },
  
    created() { this.getSale(); },
  
    methods: {
      getSale() {
        this.loading = true;
        axios.get(`/inventory/sales/${this.$route.params.id}`)
          .then(response => {
            this.loading     = false;
            this.sales       = response.data.data;
            this.sales_items = response.data.data.sales_item;
            this.sales_array = this.sales_items.map(s => {
              const taxes = s?.product?.taxes ?? [];
              const tax = taxes.reduce(
                (sum, t) => parseFloat(sum) + (s.selling_price * parseFloat(t.tax_percentage) / 100), 0
              );
              return {
                product_name:  s?.product?.product_name,
                selling_price: s?.selling_price,
                variant_name:  s?.variant?.variant_name ?? s?.product?.uom?.name ?? 'N/A',
                quantity:      s?.quantity,
                tax,
                tax_names:     taxes.map(t => t.tax_name).filter(Boolean),
                discount:      parseFloat(s?.discount ?? 0),
                total:         ((s?.selling_price * s?.quantity) + tax) - parseFloat(s?.discount ?? 0),
              };
            });
          })
          .catch(() => { this.loading = false; this.toast.error('Something went wrong'); });
      },
  
      createPDF(batch_code) {
        const pdf     = new jsPDF({ unit:'pt', orientation:'l', lineHeight:1.2 });
        const element = document.getElementById('pdf');
        html2canvas(element).then(canvas => {
          const image = canvas.toDataURL('image/png');
          pdf.addImage(image, 'JPEG', 0, 0, element.offsetWidth, element.offsetHeight);
          pdf.save(batch_code + '.pdf');
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  /* ── Skeleton ── */
  .skeleton-grid  { display:grid; grid-template-columns:repeat(5,1fr); gap:16px; }
  .skeleton       { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--table { height:220px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  
  /* ── Summary Grid ── */
  .summary-grid  { display:grid; grid-template-columns:repeat(5,1fr); gap:20px; }
  .summary-item  { display:flex; flex-direction:column; gap:6px; }
  .summary-label { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .summary-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  
  /* ── Divider ── */
  .section-divider { height:1px; background:#f0f4f8; margin:22px 0; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Batch / Store / User ── */
  .batch-pill { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .batch-code { font-family:monospace; font-size:13px; font-weight:700; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; }
  .store-tag  { display:inline-block; background:#f4f7fb; color:#4a5e78; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:12.5px; }
  .user-cell  { display:flex; align-items:center; gap:8px; font-size:13.5px; }
  .user-avatar { width:26px; height:26px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; text-transform:uppercase; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  .fw-bold         { font-weight:700; }
  
  /* ── Product Cell ── */
  .product-cell   { display:flex; align-items:center; gap:10px; }
  .product-avatar { width:32px; height:32px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
  .product-name   { font-weight:600; color:#1a2b45; }
  
  /* ── Qty Badge ── */
  .qty-badge   { display:inline-block; background:#edf4ff; color:#004AAD; border-radius:20px; padding:2px 10px; font-size:12.5px; font-weight:600; }
  .amount-value { color:#004AAD; }
  
  /* ── Grand Total ── */
  .grand-total-row   { display:flex; align-items:center; justify-content:flex-end; gap:16px; margin-top:18px; padding-top:16px; border-top:1px solid #f0f4f8; }
  .grand-total-label { font-size:14px; font-weight:700; color:#7a90b0; }
  .grand-total-value { font-size:22px; font-weight:800; color:#004AAD; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  .btn-outline:disabled { opacity:.5; cursor:not-allowed; }
  
  /* ── Responsive ── */
  @media(max-width:1200px) { .summary-grid { grid-template-columns:repeat(3,1fr); } .skeleton-grid { grid-template-columns:repeat(3,1fr); } }
  @media(max-width:768px)  { .summary-grid { grid-template-columns:repeat(2,1fr); } .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) {
    .summary-grid,.skeleton-grid { grid-template-columns:1fr; }
    .form-card { padding:16px; }
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
  }
  </style>